import React, { forwardRef, useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import {
	requestInvByProtocol,
	requestSavedData,
} from '../../../services/requests';
import { getLatestSavedRecord } from './investigacaoUtils';

const emptyInvestigacaoData = {
	coleta_dados: '',
	equipe: [],
	cronologia: [],
	planos: [],
	causas: {
		fator_paciente: '',
		fator_profissional: '',
		fator_tarefa: '',
		fator_comunicacao: '',
		fator_equipamento: '',
		fator_ambiente: '',
		fator_organizacional: '',
		fator_treinamento: '',
	},
};

const normalizeEmptyValue = (value) => {
	if (
		value === 'NÃO PREENCHIDO' ||
		value === '00:00:00' ||
		value === '2000-01-01' ||
		value === null ||
		value === undefined
	) {
		return '';
	}

	return value;
};

const formatDateBr = (value) => {
	if (!value) return '';
	const onlyDate = String(value).split('T')[0];
	const parts = onlyDate.split('-');
	if (parts.length !== 3) return value;
	const [year, month, day] = parts;
	return `${day}/${month}/${year}`;
};

const formatTimeBr = (value) => {
	if (!value) return '';
	const [hour = '', minute = ''] = String(value).split(':');
	if (!hour || !minute) return value;
	return `${hour}:${minute}`;
};

const formatStatus = (value) => {
	const statusMap = {
		PENDENTE: 'Pendente',
		EM_ANDAMENTO: 'Em andamento',
		CONCLUIDO: 'Concluído',
		CANCELADO: 'Cancelado',
	};

	return statusMap[value] || value || '';
};

const avoidPageBreakStyle = {
	breakInside: 'avoid',
	pageBreakInside: 'avoid',
	WebkitColumnBreakInside: 'avoid',
};

const loadInvestigacaoData = async (notificacao) => {
	if (!notificacao?.protocolo) return emptyInvestigacaoData;

	const userCookie = Cookies.get('user');
	if (!userCookie) return emptyInvestigacaoData;

	const { token } = JSON.parse(userCookie);
	if (!token) return emptyInvestigacaoData;

	const invByProtocol = await requestInvByProtocol(
		token,
		notificacao.protocolo,
	);

	if (
		!Array.isArray(invByProtocol) ||
		invByProtocol.length === 0 ||
		invByProtocol?.error
	) {
		return emptyInvestigacaoData;
	}

	const invId = invByProtocol[0]?.id;
	if (!invId) return emptyInvestigacaoData;

	const [
		equipeSaved,
		planosSaved,
		cronologiaSaved,
		causasSaved,
		coletaSaved,
	] = await Promise.all([
		requestSavedData(token, invId, 'equipe'),
		requestSavedData(token, invId, 'planos-acao'),
		requestSavedData(token, invId, 'cronologia'),
		requestSavedData(token, invId, 'causas'),
		requestSavedData(token, invId, 'coleta-dados'),
	]);
	const latestCausas = getLatestSavedRecord(causasSaved);

	return {
		coleta_dados:
			Array.isArray(coletaSaved) && coletaSaved.length > 0
				? normalizeEmptyValue(coletaSaved[0]?.dados_coletados)
				: '',
		equipe:
			Array.isArray(equipeSaved) && equipeSaved.length > 0
				? equipeSaved.map((membro) => ({
						nome_completo: normalizeEmptyValue(
							membro.nome_completo,
						),
						cargo: normalizeEmptyValue(membro.cargo),
						setor: normalizeEmptyValue(membro.setor),
						email: normalizeEmptyValue(membro.email),
					}))
				: [],
		planos:
			Array.isArray(planosSaved) && planosSaved.length > 0
				? planosSaved.map((plano) => ({
						causas_prioritarias: normalizeEmptyValue(
							plano.causas_prioritarias,
						),
						acoes_planejadas: normalizeEmptyValue(
							plano.acoes_planejadas,
						),
						responsavel: normalizeEmptyValue(
							plano.responsabilidade,
						),
						prazo: normalizeEmptyValue(
							plano.prazo === 32767 ? '' : plano.prazo,
						),
						status: normalizeEmptyValue(plano.status),
					}))
				: [],
		cronologia:
			Array.isArray(cronologiaSaved) && cronologiaSaved.length > 0
				? [...cronologiaSaved]
						.sort((a, b) => a.id - b.id)
						.map((cron) => ({
							evento: normalizeEmptyValue(cron.evento),
							data: normalizeEmptyValue(cron.data_evento),
							hora: normalizeEmptyValue(cron.hora_evento),
							setor: normalizeEmptyValue(cron.local),
							descricao: normalizeEmptyValue(cron.descricao),
						}))
				: [],
		causas:
			latestCausas
				? {
						fator_paciente: normalizeEmptyValue(
							latestCausas.fatores_paciente,
						),
						fator_profissional: normalizeEmptyValue(
							latestCausas.fatores_profissionais,
						),
						fator_tarefa: normalizeEmptyValue(
							latestCausas.fatores_tarefa,
						),
						fator_comunicacao: normalizeEmptyValue(
							latestCausas.fatores_comunicacao,
						),
						fator_equipe: normalizeEmptyValue(
							latestCausas.fatores_equipe,
						),
						fator_equipamento: normalizeEmptyValue(
							latestCausas.fatores_equipamento,
						),
						fator_ambiente: normalizeEmptyValue(
							latestCausas.fatores_ambiente,
						),
						fator_organizacional: normalizeEmptyValue(
							latestCausas.fatores_organizacionais,
						),
						fator_treinamento: normalizeEmptyValue(
							latestCausas.fatores_treinamento,
						),
					}
				: emptyInvestigacaoData.causas,
	};
};

const PdfField = ({ label, value, className = '' }) => (
	<div className={`flex min-w-0 w-full flex-col gap-1 ${className}`}>
		<p className='w-full px-2 text-left text-[#004A80] font-medium text-[13.5px] leading-[16px]'>
			{label}
		</p>
		<div className='min-h-[28px] w-full overflow-hidden px-2 py-1 text-left whitespace-pre-wrap break-all text-[11px] leading-[14px]'>
			{value || 'não informado'}
		</div>
	</div>
);

const PdfValueOnly = ({ value, className = '' }) => (
	<div
		className={`min-h-[28px] w-full overflow-hidden px-2 py-1 text-left whitespace-pre-wrap break-all text-[11px] leading-[14px] ${className}`}>
		{value || 'não informado'}
	</div>
);

const NewInvestigacaoPDF = forwardRef(({ notificacao, onReady }, ref) => {
	const [formData, setFormData] = useState(emptyInvestigacaoData);
	const protocolo = notificacao?.protocolo;
	const onReadyRef = useRef(onReady);

	useEffect(() => {
		onReadyRef.current = onReady;
	}, [onReady]);

	useEffect(() => {
		let isMounted = true;
		const handleLoad = async () => {
			try {
				setFormData(emptyInvestigacaoData);
				const result = await loadInvestigacaoData({ protocolo });
				if (isMounted) setFormData(result);
			} catch (error) {
				console.error('Erro ao carregar investigação para PDF:', error);
			} finally {
				if (isMounted) onReadyRef.current?.();
			}
		};

		handleLoad();
		return () => {
			isMounted = false;
		};
	}, [protocolo]);

	const fatores = [
		{ label: 'Fatores do paciente', value: formData.causas.fator_paciente },
		{
			label: 'Fatores dos profissionais',
			value: formData.causas.fator_profissional,
		},
		{
			label: 'Fatores da tarefa / atividade',
			value: formData.causas.fator_tarefa,
		},
		{
			label: 'Fatores de comunicação',
			value: formData.causas.fator_comunicacao,
		},
		{
			label: 'Fatores de equipamentos',
			value: formData.causas.fator_equipamento,
		},
		{ label: 'Fatores do ambiente', value: formData.causas.fator_ambiente },
		{
			label: 'Fatores organizacionais',
			value: formData.causas.fator_organizacional,
		},
		{
			label: 'Fatores de treinamento / educação',
			value: formData.causas.fator_treinamento,
		},
	];

	return (
		<div
			ref={ref}
			className='p-[16px] w-[800px] bg-white font-inter break-inside-avoid page-break-inside-avoid'>
			<div className='flex flex-col gap-[16px] mt-[16px]'>
				<section
					data-pdf-block='true'
					className='border border-[#E3E3E3] rounded-md'>
					<h2 className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1 pb-6'>
						EQUIPE DE INVESTIGAÇÃO
					</h2>
					<div className='p-3 flex flex-col gap-2'>
						{formData.equipe.length > 0 ? (
							formData.equipe.map((membro, index) => (
								<div
									data-pdf-block='true'
									key={`${membro.email || membro.nome_completo}-${index}`}
									className='w-full border border-[#E3E3E3] rounded-md p-2 grid grid-cols-4 gap-3 items-start'
									style={avoidPageBreakStyle}>
									<PdfField
										label='Nome'
										value={membro.nome_completo}
									/>
									<PdfField
										label='Cargo'
										value={membro.cargo}
									/>
									<PdfField
										label='Setor'
										value={membro.setor}
									/>
									<PdfField
										label='Email'
										value={membro.email}
									/>
								</div>
							))
						) : (
							<p className='px-2'>Nenhum membro informado.</p>
						)}
					</div>
				</section>

				<section
					data-pdf-block='true'
					className='border border-[#E3E3E3] rounded-md'
					style={avoidPageBreakStyle}>
					<div className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1 pb-6'>
						<h2>COLETA E ORGANIZAÇÃO DE DADOS</h2>
						<p className='text-[14px] font-normal'>
							Utilizar metodologia apropriada: entrevistas,
							consulta a prontuários ou documentos da instituição,
							busca ativa em campo, entre outras.
						</p>
					</div>
					<div className='p-3'>
						<PdfValueOnly value={formData.coleta_dados} />
					</div>
				</section>

				<section
					data-pdf-block='true'
					className='border border-[#E3E3E3] rounded-md'>
					<h2 className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1 pb-6'>
						CRONOLOGIA DO INCIDENTE
					</h2>
					<div className='p-3 flex flex-col gap-2'>
						{formData.cronologia.length > 0 ? (
							formData.cronologia.map((item, index) => (
								<div
									data-pdf-block='true'
									key={`${item.evento}-${index}`}
									className='w-full border border-[#E3E3E3] rounded-md p-2 grid grid-cols-[1.2fr_0.7fr_0.6fr_0.9fr_2fr] gap-3 items-start'
									style={avoidPageBreakStyle}>
									<PdfField
										label='Evento'
										value={item.evento}
									/>
									<PdfField
										label='Data'
										value={formatDateBr(item.data)}
									/>
									<PdfField
										label='Hora'
										value={formatTimeBr(item.hora)}
									/>
									<PdfField
										label='Local / Setor'
										value={item.setor}
									/>
									<PdfField
										label='Descrição'
										value={item.descricao}
									/>
								</div>
							))
						) : (
							<p className='px-2'>Nenhum evento informado.</p>
						)}
					</div>
				</section>

				<section
					data-pdf-block='true'
					className='border border-[#E3E3E3] rounded-md'
					style={avoidPageBreakStyle}>
					<div className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1 pb-6'>
						<h2>
							DEFINIÇÃO DAS CAUSAS - FATORES QUE CONTRIBUÍRAM PARA
							O INCIDENTE
						</h2>
					</div>
					<div className='p-3 grid grid-cols-2 gap-x-8 gap-y-2 items-start justify-between'>
						{fatores.map((fator) => (
							<PdfField
								key={fator.label}
								label={fator.label}
								value={fator.value}
							/>
						))}
					</div>
				</section>

				<section
					data-pdf-block='true'
					className='border border-[#E3E3E3] rounded-md'
					style={avoidPageBreakStyle}>
					<h2 className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1 pb-6'>
						PLANO DE AÇÃO
					</h2>
					<div className='p-3 flex flex-col gap-2'>
						{formData.planos.length > 0 ? (
							formData.planos.map((plano, index) => (
								<div
									data-pdf-block='true'
									key={`${plano.responsavel}-${index}`}
									className='w-full border border-[#E3E3E3] rounded-md p-2 grid grid-cols-[1.4fr_1.4fr_0.9fr_0.7fr_0.8fr] gap-3 items-start'
									style={avoidPageBreakStyle}>
									<PdfField
										label='Causas prioritárias'
										value={plano.causas_prioritarias}
									/>
									<PdfField
										label='Ações planejadas'
										value={plano.acoes_planejadas}
									/>
									<PdfField
										label='Responsável'
										value={plano.responsavel}
									/>
									<PdfField
										label='Prazo (dias)'
										value={String(plano.prazo || '')}
									/>
									<PdfField
										label='Status'
										value={formatStatus(plano.status)}
									/>
								</div>
							))
						) : (
							<p className='px-2'>
								Nenhum plano de ação informado.
							</p>
						)}
					</div>
				</section>
			</div>
		</div>
	);
});

export default NewInvestigacaoPDF;
